/**
 * AI工具模板组件配置架构
 * 
 * 这个文件定义了AiToolTemplate的组件配置结构，
 * 支持动态渲染不同的功能组件和灵活的提示词拼接
 */

// 组件类型枚举
export const COMPONENT_TYPES = {
  IMAGE_UPLOAD: 'imageUpload',
  PROMPT_INPUT: 'promptInput',
  COLOR_PICKER: 'colorPicker',
  PARAMETER_SELECTOR: 'parameterSelector',
  STYLE_SELECTOR: 'styleSelector',
  SIZE_SELECTOR: 'sizeSelector',
  TAG_SELECTOR: 'tagSelector',
  PLACE_PICKER: 'placePicker',
  CUSTOM: 'custom'
};

// 提示词拼接策略
export const PROMPT_STRATEGIES = {
  PREFIX: 'prefix',           // 前缀拼接：prefix + userInput
  PREFIX_ONLY: 'prefix_only', // 仅前缀拼接：prefix + userInput (与PREFIX相同，但更明确)
  SUFFIX: 'suffix',           // 后缀拼接：userInput + suffix
  TEMPLATE: 'template',       // 模板替换：template中的{input}被替换
  COMBINE: 'combine',         // 组合拼接：多个组件的值按规则组合
  CUSTOM: 'custom'            // 自定义函数处理
};

// 工具ID枚举
export const TOOL_IDS = {
  WEDDING_PHOTO: 'wedding-photo',
  PHOTO_RESTORATION: 'restored-image',
  GROUP_PHOTO: 'group-photo',
  WORLD_VIEW: 'world-view',
  WATERCOLOR: 'watercolor',
  SKETCH: 'sketch',
  PROFESSIONAL_PORTRAIT: 'professional-portrait',
  POP_ART_PORTRAIT: 'pop-art-portrait',
  OIL_PAINTING: 'oil-painting',
  ID_PHOTO_GENERATION: 'id-photo-generation',
  CYBERPUNK: 'cyberpunk',
  ANIME_STYLE: 'anime-style',
  LOGO_GENERATION: 'logo-generation',
  IMAGE_MATTING: 'image-matting',
  EMOJI_PACK: 'emoji-pack'
};

/**
 * 组件配置示例结构
 */
export const createComponentConfig = ({
  // 基础信息
  toolName,
  toolIcon,
  toolDescription,
  gradientColors = ['#4CAF50', '#00e5b8'],
  
  // API配置
  apiEndpoint = '/generate-image',
  apiParams = {},
  
  // 组件配置
  components = [],
  
  // 提示词配置
  promptConfig = {},
  
  // 额外参数
  additionalParams = {},
  
  // 布局配置
  layout = 'default'
}) => ({
  // 基础信息
  toolName,
  toolIcon,
  toolDescription,
  gradientColors,
  
  // API配置
  api: {
    endpoint: apiEndpoint,
    params: apiParams
  },
  
  // 组件配置数组
  components,
  
  // 提示词配置
  prompt: promptConfig,
  
  // 额外参数
  additionalParams,
  
  // 布局配置
  layout
});

/**
 * 单个组件配置结构
 */
export const createComponentItem = ({
  id,                          // 组件唯一标识
  type,                        // 组件类型
  title,                       // 组件标题
  description,                 // 组件描述
  required = false,            // 是否必填
  defaultValue = null,         // 默认值
  validation = null,           // 验证规则
  props = {},                  // 组件特定属性
  promptKey = null,            // 在提示词中的键名
  promptTemplate = null,       // 组件在提示词中的拼接模板，如 "with background color {value}"
  order = 0,                   // 显示顺序
  visible = true,              // 是否显示组件
  height = null                // 组件高度
}) => ({
  id,
  type,
  title,
  description,
  required,
  defaultValue,
  validation,
  props,
  promptKey,
  promptTemplate,
  order,
  visible,
  height
});

/**
 * 提示词配置结构
 */
export const createPromptConfig = ({
  strategy = PROMPT_STRATEGIES.PREFIX,
  template = '',
  prefix = '',
  suffix = '',
  combineRules = [],
  customHandler = null
}) => ({
  strategy,
  template,
  prefix,
  suffix,
  combineRules,
  customHandler
});

/**
 * Logo生成工具配置示例
 */
export const logoGenerationConfig = {
  toolName: 'Logo Generator',
  toolIcon: '🎨',
  toolDescription: 'Generate professional logo designs using AI technology',
  gradientColors: ['#FF6B6B', '#4ECDC4'],
  
  api: {
    endpoint: '/generate-image',
    params: {
      width: 1024,
      height: 1024,
      model: 'nano',
      steps: 30,
      cfgScale: 8.0
    }
  },
  
  components: [
    {
      id: 'logoPrompt',
      type: COMPONENT_TYPES.PROMPT_INPUT,
      title: 'Logo Description',
      description: 'Describe the logo you want to create',
      required: true,
      props: {
        placeholder: 'Enter your logo description...',
        minHeight: 120,
        suggestions: [
          'Modern minimalist tech company logo, blue tones',
          'Creative art studio logo, colorful brush effects',
          'Minimalist coffee shop logo, brown tones, coffee bean elements'
        ]
      },
      promptKey: 'main',
      order: 1
    },
    {
      id: 'logoStyle',
      type: COMPONENT_TYPES.PARAMETER_SELECTOR,
      title: 'Logo Style',
      description: 'Choose the style for your logo',
      required: false,
      defaultValue: 'modern',
      props: {
        options: [
          { value: 'modern', label: 'Modern', description: 'Clean and contemporary' },
          { value: 'classic', label: 'Classic', description: 'Timeless and elegant' },
          { value: 'minimal', label: 'Minimal', description: 'Simple and clean' },
          { value: 'creative', label: 'Creative', description: 'Artistic and unique' }
        ],
        displayType: 'cards' // 'dropdown', 'cards', 'buttons'
      },
      promptKey: 'style',
      order: 2
    }
  ],
  
  prompt: {
    strategy: PROMPT_STRATEGIES.TEMPLATE,
    template: 'You are a professional logo designer. Create a {style} style logo based on this description: {main}. Make it professional and suitable for business use.',
    combineRules: [
      {
        keys: ['main', 'style'],
        template: 'You are a professional logo designer. Create a {style} style logo based on this description: {main}. Make it professional and suitable for business use.'
      }
    ]
  },
  
  layout: 'default'
};

/**
 * 图片编辑工具配置示例
 */
export const imageEditConfig = {
  toolName: 'Image Editor',
  toolIcon: '✏️',
  toolDescription: 'Edit and enhance your images with AI',
  gradientColors: ['#667eea', '#764ba2'],
  
  api: {
    endpoint: '/edit-image',
    params: {
      width: 512,
      height: 512,
      steps: 20,
      cfgScale: 7.5,
      model: 'nano'
    }
  },
  
  components: [
    {
      id: 'sourceImage',
      type: COMPONENT_TYPES.IMAGE_UPLOAD,
      title: 'Upload Image',
      description: 'Upload the image you want to edit',
      required: true,
      props: {
        acceptedFormats: ['jpg', 'png', 'gif'],
        maxSize: 10 * 1024 * 1024, // 10MB
        preview: true
      },
      promptKey: 'image',
      order: 1
    },
    {
      id: 'editPrompt',
      type: COMPONENT_TYPES.PROMPT_INPUT,
      title: 'Edit Instructions',
      description: 'Describe how you want to edit the image',
      required: true,
      props: {
        placeholder: 'Describe the changes you want to make...',
        minHeight: 100
      },
      promptKey: 'instructions',
      order: 2
    }
  ],
  
  prompt: {
    strategy: PROMPT_STRATEGIES.PREFIX,
    prefix: 'You are a professional image editor. Edit the uploaded image based on these instructions: ',
    template: '{prefix}{instructions}'
  },
  
  layout: 'default'
};
